import { api, getDeviceToken } from "@awtoban/shared";
import { Button, cn } from "@awtoban/ui";
import { useQuery } from "@tanstack/react-query";
import { Delete } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { LocaleSwitcher } from "../../i18n/LocaleSwitcher";
import { useAuth } from "./AuthProvider";

interface RosterOperator {
  id: number;
  username: string;
  display_name: string;
  role: string;
  has_pin: boolean;
}
interface RosterResponse {
  station: { id: number; code: string; name: string };
  operators: RosterOperator[];
}

const PIN_MIN = 4;
const PIN_MAX = 6;

export function LockScreen() {
  const { t } = useTranslation();
  const auth = useAuth();
  const [selected, setSelected] = React.useState<RosterOperator | null>(null);

  const roster = useQuery({
    queryKey: ["shift-roster"],
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/shift/operators", {
        headers: { Authorization: `Bearer ${getDeviceToken() ?? ""}` },
      });
      if (error || !data) throw new Error("roster_failed");
      return data as unknown as RosterResponse;
    },
    staleTime: 60_000,
  });

  const station = roster.data?.station;

  return (
    <div className="flex min-h-screen flex-col bg-paper-soft">
      <header className="flex items-center gap-4 border-line-strong border-b bg-paper px-6 py-3">
        <div className="min-w-0">
          <div className="text-[11px] text-ink-muted uppercase tracking-wider">
            {t("lock.terminal")}
          </div>
          <div className="font-semibold">
            {station ? station.name : "—"}{" "}
            {station ? <span className="text-ink-muted text-sm">({station.code})</span> : null}
          </div>
        </div>
        <div className="ml-auto flex shrink-0 items-center gap-4">
          <LocaleSwitcher />
          <button
            type="button"
            onClick={auth.unbindTerminal}
            className="text-[12px] text-ink-subtle underline decoration-dotted hover:text-ink"
          >
            {t("lock.switch_station")}
          </button>
        </div>
      </header>

      <main className="mx-auto flex w-full max-w-3xl flex-1 flex-col items-center justify-center px-6 py-10">
        {selected ? (
          <PinEntry operator={selected} onBack={() => setSelected(null)} />
        ) : (
          <RosterGrid
            loading={roster.isLoading}
            operators={roster.data?.operators ?? []}
            onPick={setSelected}
          />
        )}
      </main>
    </div>
  );
}

function RosterGrid({
  loading,
  operators,
  onPick,
}: {
  loading: boolean;
  operators: RosterOperator[];
  onPick: (op: RosterOperator) => void;
}) {
  const { t } = useTranslation();
  if (loading) {
    return <div className="text-ink-muted text-sm">{t("lock.loading")}</div>;
  }
  if (operators.length === 0) {
    return <div className="text-ink-muted text-sm">{t("lock.no_operators")}</div>;
  }
  return (
    <div className="w-full">
      <h1 className="mb-6 text-center font-medium text-ink-muted text-sm uppercase tracking-wider">
        {t("lock.pick_operator")}
      </h1>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {operators.map((op) => (
          <button
            key={op.id}
            type="button"
            disabled={!op.has_pin}
            onClick={() => onPick(op)}
            className={cn(
              "flex flex-col items-start rounded-md border bg-paper p-4 text-left transition-colors",
              op.has_pin
                ? "border-line-strong hover:border-accent hover:bg-accent-soft"
                : "cursor-not-allowed border-line opacity-60",
            )}
          >
            <span className="font-semibold text-lg">{op.display_name}</span>
            <span className="mt-1 text-[12px] text-ink-muted">
              {op.has_pin ? t(`role.${op.role}`) : t("lock.no_pin")}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9"];

function PinEntry({ operator, onBack }: { operator: RosterOperator; onBack: () => void }) {
  const { t } = useTranslation();
  const auth = useAuth();
  const [pin, setPin] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);

  const submit = React.useCallback(
    async (value: string) => {
      if (value.length < PIN_MIN || submitting) return;
      setSubmitting(true);
      setError(null);
      const code = await auth.startShift(operator.id, value);
      if (code) {
        setError(t(`lock.error.${code}`, { defaultValue: t("lock.error.error") }));
        setPin("");
        setSubmitting(false);
      }
      // On success the provider switches to "active" and this screen unmounts.
    },
    [auth, operator.id, submitting, t],
  );

  const push = React.useCallback((d: string) => {
    setError(null);
    setPin((p) => (p.length >= PIN_MAX ? p : p + d));
  }, []);

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key >= "0" && e.key <= "9") push(e.key);
      else if (e.key === "Backspace") setPin((p) => p.slice(0, -1));
      else if (e.key === "Enter") void submit(pin);
      else if (e.key === "Escape") onBack();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pin, submit, onBack, push]);

  return (
    <div className="flex w-full max-w-xs flex-col items-center">
      <button
        type="button"
        onClick={onBack}
        className="mb-2 self-start text-[13px] text-ink-muted hover:text-ink"
      >
        ← {t("lock.back")}
      </button>
      <h1 className="font-semibold text-xl">{operator.display_name}</h1>
      <p className="mt-1 mb-5 text-ink-muted text-sm">{t("lock.enter_pin")}</p>

      <div className="mb-2 flex h-6 items-center gap-3">
        {Array.from({ length: PIN_MAX }, (_, i) => i).map((i) => (
          <span
            key={i}
            className={cn("h-3 w-3 rounded-full", i < pin.length ? "bg-ink" : "bg-line-strong")}
          />
        ))}
      </div>

      <div className="mb-4 h-5 text-center text-[13px] text-danger">{error ?? ""}</div>

      <div className="grid grid-cols-3 gap-3">
        {KEYS.map((k) => (
          <KeypadButton key={k} onClick={() => push(k)} disabled={submitting}>
            {k}
          </KeypadButton>
        ))}
        <KeypadButton onClick={() => setPin((p) => p.slice(0, -1))} disabled={submitting}>
          <Delete className="h-5 w-5" aria-label={t("lock.backspace")} />
        </KeypadButton>
        <KeypadButton onClick={() => push("0")} disabled={submitting}>
          0
        </KeypadButton>
        <Button
          size="xl"
          className="h-full w-full"
          disabled={pin.length < PIN_MIN || submitting}
          onClick={() => void submit(pin)}
        >
          {submitting ? "…" : t("lock.confirm")}
        </Button>
      </div>
    </div>
  );
}

function KeypadButton({
  children,
  onClick,
  disabled,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="tabular flex h-16 w-full items-center justify-center rounded-md border border-line-strong bg-paper font-semibold text-2xl transition-colors hover:bg-paper-soft active:bg-line disabled:opacity-50"
    >
      {children}
    </button>
  );
}
